import { Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Oval } from 'react-loader-spinner';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/store';
import { generateDoc } from 'store/ticket-service/asyncActions';
import { getActiveStep, getActiveTicket } from 'store/ticket-service/selector';
import { setIsExportModalOpen } from 'store/user-service/userSlice';

import { downloadURI } from 'utils/downloadURI';

import { NamespacesList } from 'core/enums/common';

import { SharedModal } from 'components/shared/Modal';

import { ReactComponent as Export } from 'assets/icons/export-lg.svg';
import { ReactComponent as CircleChecked } from 'assets/icons/rudiobutton-checked.svg';
import { ReactComponent as CircleUnchecked } from 'assets/icons/rudiobutton-unchecked.svg';

import { TicketToBase64 } from './helpers/TicketToDocx';
import { GeneratePdfFromTicket } from './helpers/TicketToPdf';

export const ExportModal = () => {
	const { t: modalTranslate } = useTranslation(NamespacesList['modals']);

	const dispatch = useAppDispatch();
	const activeTicket = useSelector(getActiveTicket);
	const activeStep = useSelector(getActiveStep);

	const [open, setOpen] = useState<boolean>(true);
	const [exportType, setExportType] = useState<string>();
	const [loading, setLoading] = useState<boolean>(false);

	const handleClose = () => {
		handleCloseModal();
		setOpen(false);
	};

	const handleCloseModal = () => {
		dispatch(setIsExportModalOpen(false));
	};

	const generateFile = async () => {
		setLoading(true);

		if (exportType === 'docx') {
			await handleDocxExport();
		} else {
			await handlePdfExport();
		}

		setLoading(false);

		handleClose();
	};

	const handleDocxExport = async () => {
		if (activeTicket) {
			// convert report html to base64, so it can be used to generate docx file
			const base64String = await TicketToBase64(activeTicket, activeStep);

			const response = await dispatch(generateDoc(base64String));

			const downloadUrl: string = response.payload as string;

			const downloadLink = document.createElement('a');

			downloadLink.href = downloadUrl;
			downloadLink.download = `${new Date().toLocaleDateString('en-GB', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			})}_BRD_${activeTicket.title}.docx`;
			downloadLink.target = '_blank';

			document.body.appendChild(downloadLink);
			downloadLink.click();
			document.body.removeChild(downloadLink);
		}
	};

	const handlePdfExport = async () => {
		if (activeTicket) {
			const ticketPdf = await GeneratePdfFromTicket(activeTicket, activeStep);

			const pdfDataUri = ticketPdf.output('datauristring');

			downloadURI(
				pdfDataUri,
				`${new Date().toLocaleDateString('en-GB', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit',
				})}_BRD_${activeTicket.title}.pdf`
			);
		}
	};

	return (
		<SharedModal isOpen={open} handleClose={handleClose}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					alignSelf: 'center',
					width: { xs: '100%', sm: '438px' },
					margin: '48px 0',
				}}
			>
				<Export />

				<Typography variant="h3" sx={{ fontSize: '24px', fontWeight: '700', marginTop: '32px' }}>
					{modalTranslate('exportReportModal.title')}
				</Typography>

				<Box
					sx={{
						width: '100%',
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<Button
						sx={{
							height: '56px',
							width: { xs: '90%', sm: '438px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							marginTop: '24px',
							color: '#000',
							justifyContent: 'flex-start',
						}}
						variant="outlined"
						color="primary"
						onClick={() => setExportType('docx')}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={exportType === 'docx'}
									icon={<CircleUnchecked />}
									checkedIcon={<CircleChecked />}
								/>
							}
							label={modalTranslate('exportReportModal.exportAsDoc')}
						/>
					</Button>

					<Button
						sx={{
							height: '56px',
							width: { xs: '90%', sm: '438px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							marginTop: '12px',
							color: '#000',
							justifyContent: 'flex-start',
						}}
						variant="outlined"
						color="primary"
						onClick={() => setExportType('pdf')}
					>
						<FormControlLabel
							control={
								<Checkbox
									checked={exportType === 'pdf'}
									icon={<CircleUnchecked />}
									checkedIcon={<CircleChecked />}
								/>
							}
							label={modalTranslate('exportReportModal.exportAsPdf')}
						/>
					</Button>
				</Box>

				<Box
					sx={{
						width: { xs: '90%', sm: '100%' },
						display: 'flex',
						justifyContent: 'space-between',
						gap: { xs: 0, sm: '24px' },
					}}
				>
					<Button
						sx={{
							height: '44px',
							width: { xs: '49%', sm: '207px' },
							borderRadius: '8px',
							textTransform: 'none',
							fontSize: '16px',
							fontWeight: '700',
							marginTop: '24px',
						}}
						variant="outlined"
						color="primary"
						onClick={handleCloseModal}
					>
						{modalTranslate('exportReportModal.cancel')}
					</Button>

					{loading ? (
						<Button
							sx={{
								height: '44px',
								width: { xs: '49%', sm: '207px' },
								borderRadius: '8px',
								marginTop: '24px',
							}}
							variant="outlined"
							color="primary"
						>
							<Oval
								height={24}
								width={24}
								color="#fff"
								wrapperStyle={{}}
								wrapperClass=""
								visible={true}
								ariaLabel="oval-loading"
								secondaryColor="#91B6FF"
								strokeWidth={5}
								strokeWidthSecondary={5}
							/>
						</Button>
					) : (
						<Button
							sx={{
								height: '44px',
								width: { xs: '49%', sm: '207px' },
								borderRadius: '8px',
								textTransform: 'none',
								fontSize: '16px',
								fontWeight: '700',
								marginTop: '24px',
							}}
							variant="contained"
							color="primary"
							disabled={!exportType}
							onClick={generateFile}
						>
							{modalTranslate('exportReportModal.confirm')}
						</Button>
					)}
				</Box>
			</Box>
		</SharedModal>
	);
};
